// CRM tasks and follow-ups (Blue Market only): a client's Tasks tab, its "Next up" card, and "My tasks" across clients.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Icon, Modal, formatDateTime, initials, useConfirm, useToast } from '../ui';
import type { Client, CrmTask } from '../types';
import { Page, useCrumbs } from './Shell';
import { useContacts } from './Crm';

const isoDay = (d: Date) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
const today = () => isoDay(new Date());
const dayLabel = (d: string) => {
  const t = today(), tomorrow = isoDay(new Date(Date.now() + 86400_000));
  if (d === t) return 'Today';
  if (d === tomorrow) return 'Tomorrow';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: d < isoDay(new Date(Date.now() + 6 * 86400_000)) && d > t ? 'long' : undefined, day: 'numeric', month: 'short' });
};
const at8 = (day: string) => new Date(`${day}T08:00:00`).toISOString();

type Reminder = '' | 'due' | 'before' | 'hour' | 'tomorrow' | 'custom';
function reminderAt(kind: Reminder, due: string, custom: string): string | null {
  if (kind === 'due' && due) return at8(due);
  if (kind === 'before' && due) return at8(isoDay(new Date(new Date(due + 'T00:00:00').getTime() - 86400_000)));
  if (kind === 'hour') return new Date(Date.now() + 3600_000).toISOString();
  if (kind === 'tomorrow') return at8(isoDay(new Date(Date.now() + 86400_000)));
  if (kind === 'custom' && custom) return new Date(custom).toISOString();
  return null;
}

function DueChip({ t }: { t: CrmTask }) {
  if (!t.due_on) return null;
  const late = !t.done && t.due_on < today();
  return <span className={`due ${late ? 'late' : ''}`}><Icon name="calendar" className="ico xs" />{late ? `Overdue · ${dayLabel(t.due_on)}` : dayLabel(t.due_on)}</span>;
}

export function TaskRow({ t, onOpen, showClient }: { t: CrmTask; onOpen: () => void; showClient?: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const toggle = async () => {
    try { await patch(`/api/tasks/${t.id}`, { done: !t.done }); qc.invalidateQueries({ queryKey: ['tasks'] }); if (!t.done) toast('Done'); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <div className={`task-row ${t.done ? 'done' : ''}`}>
      <button className={`task-check ${t.done ? 'on' : ''}`} onClick={toggle} aria-label={t.done ? 'Mark not done' : 'Mark done'} title={t.done ? 'Mark not done' : 'Mark done'}>{t.done ? '✓' : ''}</button>
      <button className="task-main" onClick={onOpen}>
        <span className="task-title">{t.title}</span>
        <span className="task-meta">
          {showClient && t.client && <span className="muted tiny">{t.client.name}</span>}
          {t.contact && <span className="muted tiny">with {t.contact.name}</span>}
          <DueChip t={t} />
          {t.remind_at && !t.done && !t.reminded && <span className="muted tiny" title={`Reminder ${formatDateTime(t.remind_at)}`}><Icon name="bell" className="ico xs" /></span>}
        </span>
      </button>
      {t.assignee && <span className="avatar xs staff" title={t.assignee.name}>{initials(t.assignee.name)}</span>}
    </div>
  );
}

// ---------- a client's Tasks tab ----------

export function TasksTab({ client }: { client: Client }) {
  const { can } = useAuth();
  const { data } = useQuery({ queryKey: ['tasks', client.id, 'open'], queryFn: () => get<{ tasks: CrmTask[]; people: { id: string; name: string }[] }>(`/api/clients/${client.id}/tasks`) });
  const [showDone, setShowDone] = useState(false);
  const { data: done } = useQuery({ queryKey: ['tasks', client.id, 'done'], queryFn: () => get<{ tasks: CrmTask[] }>(`/api/clients/${client.id}/tasks?done=true`), enabled: showDone });
  const [editing, setEditing] = useState<CrmTask | 'new' | null>(null);
  return (
    <section className="card">
      <div className="card-head"><h2>Tasks</h2><span className="grow" />{can('crm.edit') && <button className="btn sm" onClick={() => setEditing('new')}>Add a task</button>}</div>
      {data?.tasks.map((t) => <TaskRow key={t.id} t={t} onOpen={() => setEditing(t)} />)}
      {data && !data.tasks.length && <div className="empty">Nothing to follow up. Add a task to remember the next step with {client.name}.</div>}
      <button className="btn ghost sm block" style={{ borderRadius: 0, height: 38 }} onClick={() => setShowDone(!showDone)}>{showDone ? 'Hide done' : 'Show done'}</button>
      {showDone && done?.tasks.map((t) => <TaskRow key={t.id} t={t} onOpen={() => setEditing(t)} />)}
      {editing && <TaskDialog clientId={client.id} task={editing === 'new' ? null : editing} people={data?.people ?? []} onClose={() => setEditing(null)} />}
    </section>
  );
}

export function TaskDialog({ clientId, task, people, onClose }: { clientId: string; task: CrmTask | null; people: { id: string; name: string }[]; onClose: () => void }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { data: contacts = [] } = useContacts(clientId);
  const { data: peopleData } = useQuery({ queryKey: ['tasks', clientId, 'open'], queryFn: () => get<{ tasks: CrmTask[]; people: { id: string; name: string }[] }>(`/api/clients/${clientId}/tasks`), enabled: !people.length });
  const staff = people.length ? people : peopleData?.people ?? [];
  const [f, setF] = useState({ title: task?.title ?? '', notes: task?.notes ?? '', due_on: task?.due_on ?? '', assignee_id: task?.assignee?.id ?? me?.id ?? '',
                               contact_id: task?.contact?.id ?? '' });
  const [rem, setRem] = useState<Reminder>(task?.remind_at ? 'custom' : '');
  const [custom, setCustom] = useState(() => { if (!task?.remind_at) return ''; const d = new Date(task.remind_at); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); });
  const edit = can('crm.edit');
  const save = useMutation({
    mutationFn: () => {
      const body = { title: f.title, notes: f.notes, due_on: f.due_on || null, assignee_id: f.assignee_id || null, contact_id: f.contact_id || null,
                     remind_at: reminderAt(rem, f.due_on, custom) };
      return task ? patch(`/api/tasks/${task.id}`, body) : post(`/api/clients/${clientId}/tasks`, body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); onClose(); toast(task ? 'Task saved' : 'Task added'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const remove = async () => {
    if (!(await confirm('Delete this task?', { ok: 'Delete', danger: true }))) return;
    try { await del(`/api/tasks/${task!.id}`); qc.invalidateQueries({ queryKey: ['tasks'] }); onClose(); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title={task ? 'Task' : 'Add a task'} onClose={onClose} width={520}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (f.title.trim()) save.mutate(); }}>
        <label className="field"><span>What needs doing</span>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={200} required autoFocus={!task} disabled={!edit} placeholder="e.g. Call Lerato about the proposal" />
        </label>
        <div className="grid-fields">
          <label className="field"><span>Due</span><input className="input" type="date" value={f.due_on} onChange={(e) => setF({ ...f, due_on: e.target.value })} disabled={!edit} /></label>
          <label className="field"><span>For</span>
            <select className="input" value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e.target.value })} disabled={!edit}>
              <option value="">Nobody</option>{staff.map((p) => <option key={p.id} value={p.id}>{p.id === me?.id ? `${p.name} (you)` : p.name}</option>)}
            </select>
          </label>
          <label className="field"><span>About</span>
            <select className="input" value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} disabled={!edit}>
              <option value="">No one in particular</option>{contacts.filter((c) => !c.archived || c.id === f.contact_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="field"><span>Remind</span>
            <select className="input" value={rem} onChange={(e) => setRem(e.target.value as Reminder)} disabled={!edit}>
              <option value="">No reminder</option>
              {f.due_on && <option value="due">On the day, 08:00</option>}
              {f.due_on && <option value="before">The day before, 08:00</option>}
              <option value="hour">In an hour</option>
              <option value="tomorrow">Tomorrow, 08:00</option>
              <option value="custom">At a time I choose</option>
            </select>
          </label>
        </div>
        {rem === 'custom' && <input className="input" type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="Reminder time" disabled={!edit} />}
        <label className="field"><span>Notes</span><textarea className="input" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={10000} disabled={!edit} /></label>
        <div className="row" style={{ gap: 8 }}>
          {task && edit && <button type="button" className="btn ghost sm danger" onClick={remove}>Delete</button>}
          {task?.done && <span className="muted tiny">Done by {task.done_by} · {formatDateTime(task.done_at!)}</span>}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>{edit ? 'Cancel' : 'Close'}</button>
          {edit && <button className="btn primary" disabled={!f.title.trim() || save.isPending}>{save.isPending && <span className="spin" />}Save</button>}
        </div>
      </form>
      {dialog}
    </Modal>
  );
}

/** "Next up" on a client's overview: the first few open tasks. */
export function NextUp({ client, go }: { client: Client; go: (tab: string) => void }) {
  const { data } = useQuery({ queryKey: ['tasks', client.id, 'open'], queryFn: () => get<{ tasks: CrmTask[]; people: { id: string; name: string }[] }>(`/api/clients/${client.id}/tasks`) });
  const [editing, setEditing] = useState<CrmTask | null>(null);
  const tasks = data?.tasks.slice(0, 4) ?? [];
  return (
    <section className="card pad stack" style={{ gap: 6 }}>
      <div className="row"><h2 className="grow">Next up</h2><button className="link-btn small" onClick={() => go('tasks')}>All</button></div>
      {tasks.map((t) => <TaskRow key={t.id} t={t} onOpen={() => setEditing(t)} />)}
      {data && !tasks.length && <span className="muted small">Nothing to follow up.</span>}
      {editing && <TaskDialog clientId={client.id} task={editing} people={data?.people ?? []} onClose={() => setEditing(null)} />}
    </section>
  );
}

// ---------- My tasks ----------

export function MyTasks() {
  const setCrumbs = useCrumbs();
  useEffect(() => setCrumbs([{ label: 'My tasks' }]), [setCrumbs]);
  const { data } = useQuery({ queryKey: ['tasks', 'mine'], queryFn: () => get<{ tasks: CrmTask[] }>('/api/tasks/mine') });
  const [editing, setEditing] = useState<CrmTask | null>(null);
  const groups = useMemo(() => {
    const t = today(), week = isoDay(new Date(Date.now() + 7 * 86400_000));
    const open = (data?.tasks ?? []).filter((x) => !x.done);
    return [
      { key: 'overdue', label: 'Overdue', tasks: open.filter((x) => x.due_on && x.due_on < t) },
      { key: 'today', label: 'Today', tasks: open.filter((x) => x.due_on === t) },
      { key: 'week', label: 'This week', tasks: open.filter((x) => x.due_on && x.due_on > t && x.due_on <= week) },
      { key: 'later', label: 'Later', tasks: open.filter((x) => x.due_on && x.due_on > week) },
      { key: 'none', label: 'No date', tasks: open.filter((x) => !x.due_on) },
      { key: 'done', label: 'Done this week', tasks: (data?.tasks ?? []).filter((x) => x.done) },
    ].filter((g) => g.tasks.length);
  }, [data]);
  return (
    <Page title="My tasks">
      {groups.map((g) => (
        <section key={g.key} className={`card task-group ${g.key}`} style={{ marginBottom: 14 }}>
          <div className="card-head"><h2>{g.label}</h2><span className="muted tiny">{g.tasks.length}</span></div>
          {g.tasks.map((t) => <TaskRow key={t.id} t={t} showClient onOpen={() => setEditing(t)} />)}
        </section>
      ))}
      {data && !groups.length && <div className="card empty">Nothing assigned to you. Tasks you add on a client's page, or that someone gives you, show up here.</div>}
      {editing && <TaskDialog clientId={editing.client_id} task={editing} people={[]} onClose={() => setEditing(null)} />}
      {data && data.tasks.length > 0 && <p className="muted tiny">Tasks belong to clients: open one to see it in context, or go to <Link to="/clients">Clients</Link>.</p>}
    </Page>
  );
}

/** How many of my tasks need attention (overdue or today), for the sidebar. */
export function useTasksDue(enabled: boolean) {
  const { data } = useQuery({ queryKey: ['tasks', 'mine'], queryFn: () => get<{ tasks: CrmTask[] }>('/api/tasks/mine'), enabled, refetchInterval: 300_000 });
  return (data?.tasks ?? []).filter((t) => !t.done && t.due_on && t.due_on <= today()).length;
}
